import pickBy from 'lodash/pickBy';
import groupBy from 'lodash/groupBy';
import { createSelector } from 'reselect';
import { selectUser } from './userSelectors';
import { SERVICE_TYPE } from '~/constant';

const selectShoppingCart = (state) => state.shoppingCart;
const shoppingCartUI = createSelector(selectShoppingCart, (cart) => cart.ui);

export const uiTips = createSelector(shoppingCartUI, (ui) => ui.tips);

export const selectMeals = createSelector(selectShoppingCart, (shoppingCart) => shoppingCart.meals);
export const selectMealCount = createSelector(selectMeals, (meals) =>
  meals.reduce((count, meal) => {
    count += meal.quantity;
    return count;
  }, 0)
);

export const selectMealNotes = createSelector(
  selectShoppingCart,
  (shoppingCart) => shoppingCart.mealNotes
);

export const selectServiceType = createSelector(
  selectShoppingCart,
  (shoppingCart) => shoppingCart.serviceType
);

export const selectCheckoutOrder = createSelector(
  selectShoppingCart,
  (shoppingCart) => shoppingCart?.order
);

export const checkoutAmounts = createSelector(
  selectCheckoutOrder,
  (order) => order?.attributes?.amounts
);

export const checkoutAmountsNoTotals = createSelector(checkoutAmounts, (amounts) =>
  amounts
    ? amounts.filter((a) => a.name !== 'total' && a.name !== 'subtotal' && a.name !== 'tips')
    : []
);

export const checkoutAmountsGrouped = createSelector(checkoutAmountsNoTotals, (amounts) => {
  const noZeros = amounts.filter((a) => parseFloat(a.amount) > 0);
  return groupBy(noZeros, 'customerDisplayGroup');
});

export const checkoutSubtotalAmount = createSelector(checkoutAmounts, (amounts) =>
  amounts ? amounts.find((a) => a.name === 'subtotal') : null
);

export const checkoutTotalAmount = createSelector(checkoutAmounts, (amounts) =>
  amounts ? amounts.find((a) => a.name === 'total') : null
);

export const checkoutSubtotal = createSelector(checkoutSubtotalAmount, (subtotal) =>
  subtotal?.amount ? parseFloat(subtotal.amount) : null
);

export const checkoutTotal = createSelector(checkoutTotalAmount, (total) =>
  total?.amount ? parseFloat(total.amount) : null
);

/**
 * This is the total to base the "Percentage or Dollar value tips" decision on.
 */
export const checkoutTotalForTips = createSelector(
  checkoutSubtotal,
  checkoutTotal,
  (subtotal, total) => Math.max(subtotal, total)
);

export const currentTipDollarValue = createSelector(checkoutTotalForTips, uiTips, (total, tips) =>
  tips.inputType === 'cannedPercentage'
    ? parseFloat(((parseFloat(tips.value) / 100) * total).toFixed(2))
    : parseFloat(tips.value)
);

/**
 * This is the total you should actually display to the user. It DOES take service type into account.
 */
export const checkoutTotalWithTips = createSelector(
  checkoutTotal,
  currentTipDollarValue,
  selectServiceType,
  (total, tipValue, serviceType) => {
    if (serviceType === SERVICE_TYPE.DELIVERY) {
      return total + tipValue;
    }
    return total;
  }
);

export const checkouTips = createSelector(
  checkoutTotal,
  currentTipDollarValue,
  selectServiceType,
  (total, tipValue, serviceType) => {
    if (serviceType === SERVICE_TYPE.DELIVERY) {
      return tipValue;
    }
    return 0;
  }
);


export const selectCheckoutAmountDeductions = createSelector(
  selectCheckoutOrder,
  (order) => order?.relationships?.amountDeductions?.data
);

export const selectIndex = (meal) =>
  createSelector(selectMeals, (meals) => {
    return meals.findIndex((m) => hashMeal(m) === hashMeal(meal));
  });

export const selectUserAddress = createSelector(
  [selectShoppingCart, selectUser],
  (shoppingCart, user) => {
    if (!user) {
      return { address: null };
    }
    const { addressId } = shoppingCart;
    const { addresses } = user;
    return { address: addresses?.find((addr) => addr.id === addressId) };
  }
);

export const selectAllCartErrors = createSelector(
  selectShoppingCart,
  (shoppingCart) => shoppingCart.cartErrors
);

export const selectGeneralCartErrors = createSelector(selectAllCartErrors, (cartErrors) =>
  pickBy(cartErrors, (err) => !err.isLocal)
);

export const selectFinancialDisclosure = createSelector(
  selectShoppingCart,
  (shoppingCart) => shoppingCart?.order?.financialDisclosure || null
);

export const selectTips = createSelector(selectShoppingCart, (shoppingCart) => ({
  tipDollarValue: shoppingCart.tipDollarValue,
  tipDisplayValue: shoppingCart.tipDisplayValue,
}));

/**
 * Get _almost_ everything you need to render the shopping cart (except for user and payment info)
 */
export const selectShoppingCartData = createSelector(selectShoppingCart, (
  /** @type {ShoppingCartState.State} */ shoppingCart
) => {
  const {
    stop,
    shuttleDate,
    addressId,
    deliveryGroup,
    timeLoaded,
    times,
    time,
    serviceType,
    tips,
    promoCode,
    meals,
    mealNotes,
    note,
    isPlacingOrder,
  } = shoppingCart;
  return {
    stop,
    shuttleDate,
    addressId,
    deliveryGroup,
    time,
    times,
    timeLoaded,
    serviceType,
    tips,
    promoCode,
    meals,
    mealNotes,
    note,
    isPlacingOrder,
  };
});

export const hashMeal = (meal) => {
  const { quantity } = meal;
  let identifier = String(meal.id) + '.' + String(1 << quantity);
  const { customizationOptions } = meal;
  if (customizationOptions) {
    identifier = Object.values(customizationOptions)
      // TODO: this sort should return a number. something like c1.id.localeCompater(c2.id)
      .sort((c1, c2) => c1.id < c2.id)
      .reduce((acc, option) => {
        return acc + (new String(option.id) + '.' + new String(option.quantity));
      }, identifier);
  }
  return identifier;
};
